/**
 * Voxel utilities - Common helper functions
 */

import { Vector3 } from 'three';
import type { Polygon2D, HeightMapData } from './types';

/**
 * Build a 2D polygon from a 3D trim curve
 */
export function buildPolygon2D(points: Vector3[]): Polygon2D {
  const polygon: Array<[number, number]> = [];
  let minX = Infinity, maxX = -Infinity;
  let minY = Infinity, maxY = -Infinity;
  let sumZ = 0;

  for (const p of points) {
    polygon.push([p.x, p.y]);
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y);
    sumZ += p.z;
  }

  // Close polygon if not closed
  if (polygon.length > 0) {
    const first = polygon[0];
    const last = polygon[polygon.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) {
      polygon.push([first[0], first[1]]);
    }
  }

  return {
    points: polygon,
    bounds: { minX, maxX, minY, maxY },
    avgZ: points.length > 0 ? sumZ / points.length : 0,
  };
}

/**
 * Check if a 2D point is inside a polygon (ray casting algorithm)
 */
export function pointInPolygon(x: number, y: number, polygon: Polygon2D): boolean {
  const pts = polygon.points;
  let inside = false;
  
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const xi = pts[i][0], yi = pts[i][1];
    const xj = pts[j][0], yj = pts[j][1];
    
    const intersect = (yi > y) !== (yj > y) &&
      x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-10) + xi;
    
    if (intersect) inside = !inside;
  }
  
  return inside;
}

/**
 * Compute signed distance from a point to a 2D polygon boundary
 * Negative = inside, Positive = outside
 */
export function distanceToPolygon(x: number, y: number, polygon: Polygon2D): number {
  const pts = polygon.points;
  let minDist = Infinity;

  // Find minimum distance to any edge
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const x1 = pts[j][0], y1 = pts[j][1];
    const x2 = pts[i][0], y2 = pts[i][1];
    
    const dist = distanceToSegment(x, y, x1, y1, x2, y2);
    minDist = Math.min(minDist, dist);
  }

  // Apply sign based on inside/outside
  return pointInPolygon(x, y, polygon) ? -minDist : minDist;
}

/**
 * Distance from point to line segment
 */
function distanceToSegment(
  px: number, py: number,
  x1: number, y1: number,
  x2: number, y2: number,
): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  
  if (lenSq < 1e-10) {
    // Degenerate segment (point)
    return Math.sqrt((px - x1) ** 2 + (py - y1) ** 2);
  }
  
  // Project point onto line, clamped to segment
  let t = ((px - x1) * dx + (py - y1) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  
  const nearestX = x1 + t * dx;
  const nearestY = y1 + t * dy;
  
  return Math.sqrt((px - nearestX) ** 2 + (py - nearestY) ** 2);
}

/**
 * Create a height map from a polygon (constant height)
 */
export function createHeightMapFromPolygon(
  polygon: Polygon2D,
  cellSize: number,
  height: number,
  padding = 0,
): HeightMapData {
  const { minX, maxX, minY, maxY } = polygon.bounds;
  
  const originX = minX - padding;
  const originY = minY - padding;
  const width = Math.ceil((maxX - minX + padding * 2) / cellSize) + 1;
  const heightCells = Math.ceil((maxY - minY + padding * 2) / cellSize) + 1;
  
  const values = new Float32Array(width * heightCells);
  values.fill(NaN);
  
  for (let iy = 0; iy < heightCells; iy++) {
    const wy = originY + iy * cellSize;
    for (let ix = 0; ix < width; ix++) {
      const wx = originX + ix * cellSize;
      if (pointInPolygon(wx, wy, polygon)) {
        values[iy * width + ix] = height;
      }
    }
  }
  
  return { width, height: heightCells, originX, originY, cellSize, values };
}

/**
 * Get Z value at a point by interpolating trim curve
 */
export function interpolateTrimCurveZ(
  x: number,
  y: number,
  trimPoints: Vector3[],
): number | null {
  if (trimPoints.length === 0) return null;
  
  // Find closest point on trim curve
  let minDist = Infinity;
  let closestZ = trimPoints[0].z;
  
  for (let i = 0; i < trimPoints.length; i++) {
    const p1 = trimPoints[i];
    const p2 = trimPoints[(i + 1) % trimPoints.length];
    
    // Project onto segment
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const lenSq = dx * dx + dy * dy;
    
    let t = 0;
    if (lenSq > 1e-10) {
      t = ((x - p1.x) * dx + (y - p1.y) * dy) / lenSq;
      t = Math.max(0, Math.min(1, t));
    }
    
    const nearestX = p1.x + t * dx;
    const nearestY = p1.y + t * dy;
    const nearestZ = p1.z + t * (p2.z - p1.z);
    
    const dist = Math.sqrt((x - nearestX) ** 2 + (y - nearestY) ** 2);
    if (dist < minDist) {
      minDist = dist;
      closestZ = nearestZ;
    }
  }
  
  return closestZ;
}

/**
 * Dilate a 2D height map (max filter)
 */
export function dilateHeightMap(heightMap: HeightMapData, radius: number): HeightMapData {
  const { width, height, originX, originY, cellSize, values } = heightMap;
  const result = new Float32Array(values.length);
  const r = Math.ceil(radius / cellSize);
  const rSq = r * r;

  for (let iy = 0; iy < height; iy++) {
    for (let ix = 0; ix < width; ix++) {
      let maxVal = NaN;

      for (let dy = -r; dy <= r; dy++) {
        const ny = iy + dy;
        if (ny < 0 || ny >= height) continue;

        for (let dx = -r; dx <= r; dx++) {
          const nx = ix + dx;
          if (nx < 0 || nx >= width) continue;
          if (dx * dx + dy * dy > rSq) continue; // Circular kernel

          const val = values[ny * width + nx];
          if (Number.isFinite(val)) {
            if (!Number.isFinite(maxVal) || val > maxVal) {
              maxVal = val;
            }
          }
        }
      }

      result[iy * width + ix] = maxVal;
    }
  }

  return { width, height, originX, originY, cellSize, values: result };
}

